import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {culturalAlignmentColors} from '../WorkforceProfileDetail.colors';

import {WorkforceCulturalAlignmentDimensionCard} from './WorkforceCulturalAlignmentDimensionCard';
import {WorkforceCulturalAlignmentLegend} from './WorkforceCulturalAlignmentLegend';
import {orangeColors} from '../../../../../../layouts/theme/designSystem';
import {WorkforceProfileCulturalFitTopQuestion} from '../../../types/WorkforceProfileTypes';
import {resolveOverlapPercent} from '../../../utils/WorkforceProfileDetail/workforceCulturalAlignmentUtils';

export interface Props {
  questions: WorkforceProfileCulturalFitTopQuestion[];
}

export const WorkforceCulturalAlignmentDimensions = ({questions}: Props) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceProfilesPage.detail.culturalFitAnalysis.dimensions',
  });

  const overallOverlapPercent = useMemo(() => {
    if (questions.length === 0) {
      return null;
    }

    const total = questions.reduce(
      (sum, question) =>
        sum +
        resolveOverlapPercent(question.overlap),
      0
    );

    return Math.round(total / questions.length);
  }, [questions]);

  if (questions.length === 0) {
    return null;
  }

  return (
    <Box>
      <WorkforceCulturalAlignmentLegend
        title={t('legendTitle')}
        companyLabel={t('companyLabel')}
        profileLabel={t('profileLabel')}
      />

      {overallOverlapPercent != null && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            bgcolor: orangeColors[5],
            borderRadius: '10px',
          }}
        >
          <Typography sx={{fontSize: 12, color: 'text.secondary', mb: 0.75}}>
            {t('overallAlignment')}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <Typography sx={{fontSize: 26, fontWeight: 500, color: 'text.primary', lineHeight: 1}}>
              {overallOverlapPercent}%
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: 8,
                bgcolor: 'background.paper',
                borderRadius: '4px',
                border: `0.5px solid ${orangeColors[3]}`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${overallOverlapPercent}%`,
                  borderRadius: '4px',
                  background: culturalAlignmentColors.overallAlignmentGradient,
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', md: `repeat(${Math.min(questions.length, 3)}, 1fr)`},
          gap: 1.5,
        }}
      >
        {questions.map((question) => (
          <WorkforceCulturalAlignmentDimensionCard key={question.resourceId} question={question} />
        ))}
      </Box>
    </Box>
  );
};
